import React, { useEffect, useState, useRef } from 'react';
import { horarioService } from 'services/horarioService';
import { catalogoService } from 'services/usuarioService';
import {
  HorarioImpresionResponse,
  HorarioImpresionGrado,
  HorarioImpresionSeccion,
  HorarioImpresionBloque,
  DiaSemana,
  DIA_LABEL,
} from 'types/horario.types';
import { AñoEscolar, Grado, Seccion } from 'types/usuario.types';
import { getConfig } from 'utils/appConfig';

// ============================================================
// ImpresionHorariosPage
// Genera y permite imprimir el horario de un nivel/grado/sección
// Acceso: ADMIN, DIRECTOR, SECRETARIA
// ============================================================

const NIVELES = ['INICIAL', 'PRIMARIA', 'SECUNDARIA'];
const NIVEL_LABEL: Record<string, string> = {
  INICIAL: 'Inicial', PRIMARIA: 'Primaria', SECUNDARIA: 'Secundaria',
};
const NIVEL_COLOR: Record<string, string> = {
  INICIAL: 'bg-amber-100 text-amber-700', PRIMARIA: 'bg-blue-100 text-blue-700', SECUNDARIA: 'bg-purple-100 text-purple-700',
};

const DIAS_IMPRIMIR: DiaSemana[] = ['LUNES', 'MARTES', 'MIERCOLES', 'JUEVES', 'VIERNES'];

const SpinnerIcon = () => (
  <svg className="animate-spin w-5 h-5 text-blue-600" viewBox="0 0 24 24" fill="none">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 1 8-8V0C5.373 0 0 5.373 0 12h4Z" />
  </svg>
);

// ── Tabla de horario por sección ──────────────────────────────

interface TablaSeccionProps {
  grado: HorarioImpresionGrado;
  seccion: HorarioImpresionSeccion;
  nivel: string;
  anio: number;
  institucion: string;
}

const TablaSeccion: React.FC<TablaSeccionProps> = ({ grado, seccion, nivel, anio, institucion }) => {
  // Recolectar todos los bloques de todos los días para calcular filas de tiempo únicas
  const todosLosBloques: HorarioImpresionBloque[] = DIAS_IMPRIMIR.flatMap(
    d => seccion.diasSemana[d] ?? []
  );

  // Franjas únicas ordenadas por hora de inicio
  const franjasSet = new Set(todosLosBloques.map(b => `${b.horaInicio}-${b.horaFin}`));
  const franjas = Array.from(franjasSet)
    .sort((a, b) => a.localeCompare(b))
    .map(f => { const [ini, fin] = f.split('-'); return { horaInicio: ini, horaFin: fin }; });

  const getBloques = (dia: DiaSemana, ini: string, fin: string): HorarioImpresionBloque[] =>
    (seccion.diasSemana[dia] ?? []).filter(b => b.horaInicio === ini && b.horaFin === fin);

  return (
    <div className="seccion-horario mb-8 break-inside-avoid">
      {/* Cabecera de la sección */}
      <div className="flex items-center gap-3 mb-3">
        <div className="flex items-center gap-2">
          <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${NIVEL_COLOR[nivel] ?? 'bg-slate-100 text-slate-600'}`}>
            {NIVEL_LABEL[nivel] ?? nivel}
          </span>
          <h2 className="text-base font-bold text-slate-800">
            {grado.gradoNombre} grado — Sección {seccion.seccionNombre}
          </h2>
        </div>
        <span className="text-slate-400 text-xs ml-auto">Año {anio}</span>
      </div>

      {franjas.length === 0 ? (
        <div className="text-slate-400 text-sm italic py-2">Sin horarios registrados.</div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-slate-200 shadow-sm">
          <table className="horario-tabla w-full text-xs border-collapse">
            <thead className="horario-thead">
              <tr className="bg-slate-700 text-white">
                <th className="px-3 py-2 text-left font-semibold w-28 border-r border-slate-600">
                  Hora
                </th>
                {DIAS_IMPRIMIR.map(d => (
                  <th key={d} className="px-3 py-2 text-center font-semibold border-r border-slate-600 last:border-r-0">
                    {DIA_LABEL[d]}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="horario-tbody">
              {franjas.map((franja, fi) => (
                <tr key={fi} className={`border-b border-slate-200 ${fi % 2 === 0 ? 'bg-white' : 'bg-slate-50'}`}>
                  <td className="px-3 py-2 text-slate-500 font-medium border-r border-slate-200 whitespace-nowrap">
                    {franja.horaInicio} – {franja.horaFin}
                  </td>
                  {DIAS_IMPRIMIR.map(d => {
                    const bloques = getBloques(d, franja.horaInicio, franja.horaFin);
                    return (
                      <td key={d} className="px-2 py-1.5 border-r border-slate-200 last:border-r-0 align-top">
                        {bloques.map((b, bi) => {
                          const esRecreo = b.docente === null || b.cursoNombre === 'RECREO';
                          return esRecreo ? (
                            <div key={bi} className="flex items-center justify-center h-full py-0.5">
                              <span className="inline-flex items-center gap-1 text-[10px] font-bold
                                               px-2 py-0.5 rounded-full bg-green-100 text-green-700">
                                <svg viewBox="0 0 16 16" fill="currentColor" className="w-3 h-3">
                                  <path fillRule="evenodd" d="M8 15A7 7 0 1 0 8 1a7 7 0 0 0 0 14Zm.75-10.25a.75.75 0 0 0-1.5 0v3.5c0 .414.336.75.75.75h3a.75.75 0 0 0 0-1.5H8.75v-2.75Z" clipRule="evenodd"/>
                                </svg>
                                RECREO
                              </span>
                            </div>
                          ) : (
                            <div key={bi} className="mb-1 last:mb-0">
                              <p className="font-semibold text-slate-800 leading-tight">{b.cursoNombre}</p>
                              <p className="text-slate-500 text-xs leading-tight">{b.docente}</p>
                            </div>
                          );
                        })}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pie de página invisible en pantalla, visible al imprimir */}
      <div className="pie-impresion hidden print:block text-xs text-slate-400 mt-1 text-right">
        {institucion} — SAE
      </div>
    </div>
  );
};

// ── Componente principal ──────────────────────────────────────

const ImpresionHorariosPage: React.FC = () => {
  const [añosEscolares,  setAñosEscolares]  = useState<AñoEscolar[]>([]);
  const [todosGrados,    setTodosGrados]    = useState<Grado[]>([]);
  const [todasSecciones, setTodasSecciones] = useState<Seccion[]>([]);

  const [anioId,    setAnioId]    = useState<number | ''>('');
  const [nivel,     setNivel]     = useState('');
  const [gradoId,   setGradoId]   = useState<number | ''>('');
  const [seccionId, setSeccionId] = useState<number | ''>('');

  const [resultado, setResultado] = useState<HorarioImpresionResponse | null>(null);
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState<string | null>(null);
  const printRef = useRef<HTMLDivElement>(null);

  const gradosFiltrados = todosGrados.filter(g => !nivel || g.nivel === nivel);

  const seccionesPorGrado = gradoId
    ? todasSecciones.filter(s => s.gradoId === Number(gradoId))
    : todasSecciones;
  const seccionesFiltradas = seccionesPorGrado;

  // Limpiar dependientes al cambiar filtros superiores
  useEffect(() => { setGradoId(''); setSeccionId(''); }, [nivel]);
  useEffect(() => { setSeccionId(''); }, [gradoId]);

  useEffect(() => {
    Promise.all([
      catalogoService.getAñosEscolares(),
      catalogoService.getGrados(),
      catalogoService.getSecciones(),
    ]).then(([años, grados, secciones]) => {
      setAñosEscolares(años);
      setTodosGrados(grados);
      setTodasSecciones(secciones);
      const activo = años.find(a => a.activo);
      if (activo) setAnioId(activo.idAnioEscolar);
    }).catch(() => {});
  }, []);

  const handleGenerar = async () => {
    if (!anioId || !nivel) return;
    setLoading(true); setError(null); setResultado(null);
    try {
      const data = await horarioService.obtenerImpresion({
        anioEscolarId: anioId as number,
        nivel,
        gradoId:   gradoId   ? (gradoId   as number) : undefined,
        seccionId: seccionId ? (seccionId as number) : undefined,
      });
      setResultado(data);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Error al cargar los horarios');
    } finally { setLoading(false); }
  };

  const handleImprimir = () => {
    window.print();
  };

  // Construir secciones aplanadas según filtros
  const secciones: { grado: HorarioImpresionGrado; seccion: HorarioImpresionSeccion }[] = [];
  if (resultado) {
    for (const grado of resultado.grados) {
      for (const seccion of grado.secciones) {
        secciones.push({ grado, seccion });
      }
    }
  }

  const institucion = getConfig().institucionNombre;
  const anioSeleccionado = añosEscolares.find(a => a.idAnioEscolar === anioId);

  return (
    <>
      {/* ── Estilos de impresión ── */}
      <style>{`
        /* Hoja A4 horizontal para todo el documento */
        @page {
          size: A4 landscape;
          margin: 12mm 14mm;
        }

        @media print {
          body * { visibility: hidden; }
          #horario-print-area, #horario-print-area * { visibility: visible; }
          #horario-print-area {
            position: absolute; left: 0; top: 0; width: 100%;
          }
          .no-print { display: none !important; }

          /* Cada sección ocupa su propia página */
          .seccion-horario {
            page-break-after: always;
            break-after: page;
            page-break-inside: avoid;
            break-inside: avoid;
            margin-bottom: 0;
          }
          /* Evitar salto de página extra después de la última sección */
          .seccion-horario:last-child {
            page-break-after: auto;
            break-after: auto;
          }

          /* Forzar impresión de fondos y colores */
          * {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }

          /* Encabezado de tabla */
          .horario-thead tr,
          .horario-thead th {
            background-color: #334155 !important;
            color: #ffffff !important;
          }

          /* Bordes horizontales entre filas */
          .horario-tbody tr,
          .horario-tbody td {
            border-bottom: 1px solid #cbd5e1 !important;
          }

          /* Filas alternas */
          .horario-tbody tr:nth-child(even) {
            background-color: #f8fafc !important;
          }

          /* Tabla ocupa todo el ancho de la hoja */
          .horario-tabla {
            width: 100% !important;
          }
        }
      `}</style>

      <div className="space-y-5">

        {/* ── Cabecera ── */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 no-print">
          <div>
            <h1 className="text-xl font-bold text-slate-800">Impresión de Horarios</h1>
            <p className="text-slate-400 text-sm mt-0.5">
              Genera y descarga el horario por nivel, grado o sección
            </p>
          </div>
          {resultado && (
            <button
              onClick={handleImprimir}
              className="flex items-center gap-2 bg-slate-700 hover:bg-slate-800 text-white
                         font-semibold text-sm px-4 py-2.5 rounded-xl transition-colors shadow-sm"
            >
              <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                <path fillRule="evenodd" d="M5 4v3H4a2 2 0 0 0-2 2v5a2 2 0 0 0 2 2h1v2a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1v-2h1a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-1V4a1 1 0 0 0-1-1H6a1 1 0 0 0-1 1Zm2 0h6v3H7V4Zm-1 9a1 1 0 1 0 0 2h8a1 1 0 1 0 0-2H6Zm0 3h8v2H6v-2Z" clipRule="evenodd" />
              </svg>
              Imprimir
            </button>
          )}
        </div>

        {/* ── Panel de filtros ── */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 no-print">
          <h2 className="text-sm font-semibold text-slate-700 mb-4">Parámetros de búsqueda</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">

            {/* Año escolar */}
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">
                Año Escolar <span className="text-red-500">*</span>
              </label>
              <select
                value={anioId}
                onChange={e => setAnioId(e.target.value ? Number(e.target.value) : '')}
                className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm text-slate-800
                           focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
              >
                <option value="">— Seleccionar —</option>
                {añosEscolares.map(a => (
                  <option key={a.idAnioEscolar} value={a.idAnioEscolar}>
                    {a.anio}{a.activo ? ' (activo)' : ''}
                  </option>
                ))}
              </select>
            </div>

            {/* Nivel */}
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">
                Nivel <span className="text-red-500">*</span>
              </label>
              <select
                value={nivel}
                onChange={e => setNivel(e.target.value)}
                className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm text-slate-800
                           focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
              >
                <option value="">— Seleccionar —</option>
                {NIVELES.map(n => (
                  <option key={n} value={n}>{NIVEL_LABEL[n]}</option>
                ))}
              </select>
            </div>

            {/* Grado (opcional) */}
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">
                Grado <span className="text-slate-400 font-normal">(opcional)</span>
              </label>
              <select
                value={gradoId}
                onChange={e => setGradoId(e.target.value ? Number(e.target.value) : '')}
                disabled={!nivel}
                className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm text-slate-800
                           focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white
                           disabled:bg-slate-50 disabled:text-slate-400"
              >
                <option value="">Todos los grados</option>
                {gradosFiltrados.map(g => (
                  <option key={g.idGrado} value={g.idGrado}>{g.nombre}</option>
                ))}
              </select>
            </div>

            {/* Sección (opcional) */}
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">
                Sección <span className="text-slate-400 font-normal">(opcional)</span>
              </label>
              <select
                value={seccionId}
                onChange={e => setSeccionId(e.target.value ? Number(e.target.value) : '')}
                disabled={!gradoId}
                className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm text-slate-800
                           focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white
                           disabled:bg-slate-50 disabled:text-slate-400"
              >
                <option value="">Todas las secciones</option>
                {seccionesFiltradas.map(s => (
                  <option key={s.idSeccion} value={s.idSeccion}>{s.nombre}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="flex justify-end mt-4">
            <button
              onClick={handleGenerar}
              disabled={!anioId || !nivel || loading}
              className="flex items-center gap-2 bg-blue-700 hover:bg-blue-800 disabled:bg-blue-300
                         text-white font-semibold text-sm px-5 py-2.5 rounded-xl transition-colors shadow-sm"
            >
              {loading ? <SpinnerIcon /> : (
                <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                  <path fillRule="evenodd" d="M9 3.5a5.5 5.5 0 1 0 0 11 5.5 5.5 0 0 0 0-11ZM2 9a7 7 0 1 1 12.452 4.391l3.328 3.329a.75.75 0 1 1-1.06 1.06l-3.329-3.328A7 7 0 0 1 2 9Z" clipRule="evenodd" />
                </svg>
              )}
              {loading ? 'Cargando...' : 'Generar Horario'}
            </button>
          </div>
        </div>

        {/* ── Error ── */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-red-700 text-sm no-print">
            {error}
          </div>
        )}

        {/* ── Área de impresión ── */}
        {resultado && (
          <div id="horario-print-area" ref={printRef}>

            {/* Encabezado institucional (solo visible en impresión) */}
            <div className="hidden print:block text-center mb-6">
              <p className="text-lg font-bold">{institucion}</p>
              <p className="text-sm text-slate-600">Sistema de Administración Escolar — SAE</p>
              <p className="text-sm font-semibold mt-1">
                Horario {NIVEL_LABEL[resultado.nivel] ?? resultado.nivel} — Año {resultado.anio}
              </p>
            </div>

            {/* Resumen en pantalla */}
            <div className="flex items-center gap-3 mb-5 no-print">
              <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${NIVEL_COLOR[resultado.nivel] ?? 'bg-slate-100 text-slate-600'}`}>
                {NIVEL_LABEL[resultado.nivel] ?? resultado.nivel}
              </span>
              <span className="text-slate-700 font-semibold text-sm">
                Año escolar {resultado.anio}
              </span>
              <span className="text-slate-400 text-xs">
                {secciones.length} sección{secciones.length !== 1 ? 'es' : ''}
              </span>
            </div>

            {secciones.length === 0 ? (
              <div className="bg-white rounded-2xl border border-slate-200 p-8 text-center text-slate-400">
                No se encontraron horarios para los parámetros seleccionados.
              </div>
            ) : (
              secciones.map(({ grado, seccion }) => (
                <TablaSeccion
                  key={`${grado.gradoId}-${seccion.seccionId}`}
                  grado={grado}
                  seccion={seccion}
                  nivel={resultado.nivel}
                  anio={resultado.anio}
                  institucion={institucion}
                />
              ))
            )}
          </div>
        )}

        {/* Estado vacío inicial */}
        {!resultado && !loading && !error && (
          <div className="bg-white rounded-2xl border border-dashed border-slate-300 p-10 text-center no-print">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}
              className="w-12 h-12 text-slate-300 mx-auto mb-3">
              <path strokeLinecap="round" strokeLinejoin="round"
                d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5" />
            </svg>
            <p className="text-slate-500 font-medium">Selecciona año escolar y nivel para generar el horario</p>
            <p className="text-slate-400 text-sm mt-1">Opcionalmente puedes filtrar por grado</p>
          </div>
        )}
      </div>
    </>
  );
};

export default ImpresionHorariosPage;
